/**
 * 首页提醒管理系统
 * 重构后的统一提醒管理，按顺序弹出各种提醒
 */
import {getUserLogs} from '@/api/admin/log'
import {fetchMyPolicyList, fetchPolicyList} from '@/api/insurance/policy'
import {getCheckSettleAlert, getMyChannelSettleInfo} from '@/api/insurance/settlement'
import {env, getDiffEnvConfig, isBxIe, isBxXsm} from '@/config/env'
import {EnumLogType} from '@/const/enum'
import router from '@/router/router'
import store from '@/store'
import {isChannel, isIframe, isMainChannel, isMainChannelAndInner, isZiChannel} from '@/util/channelUtil'
import {getStore, setStore} from '@/util/store'
import {MessageBox} from 'element-ui'
import {centerObjectPost} from 'meibao-insure/src/api/productCenterNew'

/**
 * 提醒队列管理器
 * 统一管理所有提醒的执行顺序
 */
class HintManager {
	constructor() {
		this.hintQueue = []
		this.isProcessing = false
	}

	/**
	 * 添加提醒到队列
	 * @param {Function} hintFunction - 提醒函数
	 * @param {string} name - 提醒名称
	 */
	addHint(hintFunction, name) {
		this.hintQueue.push({
			function: hintFunction,
			name: name,
		})
	}

	/**
	 * 开始处理提醒队列
	 */
	async processHints() {
		if (this.isProcessing) return
		this.isProcessing = true

		for (const hint of this.hintQueue) {
			try {
				console.log(`正在处理提醒: ${hint.name}`)
				await hint.function()
			} catch (error) {
				console.log(`提醒 ${hint.name} 跳过:`, error)
				// 无论成功失败都继续下一个提醒
			}
		}

		this.isProcessing = false
		this.hintQueue = []
	}
}

// 创建全局提醒管理器实例
const hintManager = new HintManager()

/**
 * 主入口函数 - 初始化所有提醒
 * 按顺序添加所有提醒到队列中
 */
export const initializeHints = () => {
	//如果是内嵌的话，则不处理
	if (isIframe() || !isChannel()) {
		return
	}
	// 按顺序添加提醒
	// hintManager.addHint(onHintJtNotices, '吉泰条款提醒')
	if ((isBxIe() && isMainChannel()) || (isBxXsm() && isZiChannel())) {
		// IE保是主渠道，新市民是子渠道
		hintManager.addHint(onHintEditSettle, '完善结算信息提醒')
	}
	//结算申请
	if (isBxIe()) {
		hintManager.addHint(onHintApplySettle, '结算申请提醒')
	}
	//续保
	hintManager.addHint(onHintRenewalNotice, '续保提醒')
	//结算预警
	if (isBxIe() && isMainChannel()) {
		hintManager.addHint(onHintSettleAlert, '结算预警提醒')
	}
	// 开始处理提醒队列
	hintManager.processHints()
}

/**
 * 吉泰条款提醒
 * @returns {Promise}
 */
export const onHintJtNotices = () => {
	return new Promise(async (resolve, reject) => {
		try {
			// 通过用户信息缓存 判断是否查看过
			const resLogList = (await getUserLogs(EnumLogType.logInsureNotices)) || {}
			const {data = []} = resLogList.data || {}
			if (data.length > 0) {
				reject('')
				return
			}

			// 获取提醒接口
			const resHint = (await centerObjectPost('/v2/index/platform/notice')) || {}
			const {brokerProtocol = null} = resHint.data.data || {}

			if (brokerProtocol) {
				await MessageBox.confirm('请及时查看最新的吉泰条款内容', '吉泰条款提醒', {
					confirmButtonText: '查看条款',
					cancelButtonText: '稍后查看',
					type: 'info',
				})
				resolve()
			} else {
				reject('无需显示吉泰条款提醒')
			}
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * 结算预警提醒 - 新增功能
 * 一天只弹出一次，使用try catch处理错误
 * @returns {Promise}
 */
export const onHintSettleAlert = () => {
	return new Promise(async (resolve, reject) => {
		try {
			// 检查今天是否已经弹过
			const today = new Date().toDateString()
			const lastShowDate = getStore({name: 'settleAlertLastShow'}) || ''

			if (lastShowDate === today) {
				reject('')
				return
			}
			// 调用结算预警接口
			const response = await getCheckSettleAlert()
			const {code, data: {code: codeNei, msg} = {}} = response.data || {}
			// 记录今天已显示
			setStore({name: 'settleAlertLastShow', content: today})
			if (code === 0 && codeNei === 1 && msg) {
				await MessageBox.confirm(msg, '结算预警', {
					confirmButtonText: '知道了',
					showCancelButton: false,
					type: 'error',
				})
				resolve()
			}
		} catch (error) {
			console.error('结算预警接口调用失败:', error)
			// 即使接口失败也要显示错误信息给用户
			let {response: {data = {}} = {}} = error || {}
			let {code = 0, msg = ''} = data
			if (msg && code === 1) {
				try {
				} catch (e) {
					// 用户关闭弹窗，继续下一个提醒
				}
			}
			reject(error)
		}
	})
}

/**
 * 完善结算信息提醒
 * @returns {Promise}
 */
export const onHintEditSettle = () => {
	return new Promise(async (resolve, reject) => {
		try {
			const shouldShowHint = await checkShouldShowSettleHint()
			if (!shouldShowHint) {
				reject('')
				return
			}
			await MessageBox.confirm('为了出单后便捷的进行结算操作，请及时完善结算信息！', '结算信息完善提醒', {
				confirmButtonText: '去填写',
				cancelButtonText: '暂不操作',
				type: 'info',
			})
			// 跳转到结算信息页面
			await router.push({
				path: router.$avueRouter.getPath({
					src: '/channel/user/editSettleInfo',
				}),
			})
			resolve()
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * 检查是否需要显示结算信息提醒
 * @returns {Promise<boolean>}
 */
const checkShouldShowSettleHint = () => {
	return new Promise(async (resolve, reject) => {
		try {
			const resChannel = (await getMyChannelSettleInfo()) || {}
			let {settleAccounts = [], settleType} = resChannel.data.data || {}
			if (settleType === 'PUBLIC') {
				// 对公结算不需要完善
				resolve(false)
				return
			}
			settleAccounts = settleAccounts.filter(({type = ''}) => {
				return `${type}` === `${settleType}` || !type
			})
			if (settleAccounts.length <= 0) {
				resolve(false)
				return
			}
			// 必须完善所有信息
			const incompleteIndex = settleAccounts.findIndex(
				({cardBackUrl = '', cardFrontUrl = '', idNo = '', mobile = '', cardNo = '', userName = ''}) =>
					!(cardBackUrl && cardFrontUrl && idNo && mobile && cardNo && userName),
			)
			resolve(incompleteIndex !== -1)
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * 结算申请提醒
 * @returns {Promise}
 */
export const onHintApplySettle = () => {
	return new Promise(async (resolve, reject) => {
		try {
			// 结算日检查
			const dayNum = new Date().getDate()
			const isSettleHint = store.getters.isShowSettleHint

			if ((isSettleHint && dayNum >= 1 && dayNum <= 3) || (dayNum >= 16 && dayNum <= 18 && isMainChannelAndInner())) {
				store.commit('SET_SHOW_SETTLE_HINT', false)
				await MessageBox.confirm('本次结算申请正在进行中，记得提醒主渠道管理员及时提交申请！', '结算提醒', {
					confirmButtonText: '知道了',
					showCancelButton: false,
					type: 'warning',
				})
				resolve()
			} else {
				reject('')
			}
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * 续保提醒
 * @returns {Promise}
 */
export const onHintRenewalNotice = () => {
	return new Promise(async (resolve, reject) => {
		try {
			let isShowHint = 0
			let dateNum = []
			// 获取我的保单数据
			const myPolicyRes =
				(await fetchMyPolicyList({
					status: 2,
					pageSize: 100,
					fields: 'status,endDate',
				})) || {}
			const {data: {data: {records = []} = {}} = {}} = myPolicyRes
			// 获取时间数据
			dateNum = getExpirationCounts(records)
			if (dateNum[0] !== 0 || dateNum[1] !== 0) {
				isShowHint = 1
			}
			if (isShowHint === 0) {
				// 再获取所有数据查询有没有续保
				const allPolicyRes = (await fetchPolicyList({status: 2, pageSize: 100})) || {}
				const {data: {data: {records: recordsAll = []} = {}} = {}} = allPolicyRes
				dateNum = getExpirationCounts(recordsAll)
				if (dateNum[0] !== 0 || dateNum[1] !== 0) {
					isShowHint = 2
				}
			}
			if (isShowHint !== 0) {
				await MessageBox.confirm(
					`您有${dateNum[0]}条保单将于3日内到期，${dateNum[1]}条保单将于7日内到期，请及时进行续保操作！`,
					'续保提醒',
					{
						confirmButtonText: '去续保',
						cancelButtonText: '稍后处理',
						type: 'warning',
					},
				)
				// 跳转到相应的保单页面
				await router.push({
					path: router.$avueRouter.getPath({
						src: isShowHint === 1 ? '/insurance/policy/index' : '/statistics/policy/index',
					}),
					query: {status: 2},
				})
				resolve()
			} else {
				reject('')
			}
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * 获取续保日期统计
 * @param {Array} records - 保单记录
 * @returns {Array} [3日内到期数量, 7日内到期数量]
 */
const getExpirationCounts = (records = []) => {
	let threeDateNum = 0
	let sevenDateNum = 0
	const oneDate = 24 * 60 * 60 * 1000
	const currentMill = new Date().getTime()

	for (const {status = -1, endDate = ''} of records) {
		if (status === 3 && endDate !== '') {
			const tempEndDate = new Date(`${endDate} 23:59:59`).getTime()
			if (tempEndDate <= currentMill + 3 * oneDate) {
				threeDateNum++
				sevenDateNum++
			} else if (tempEndDate <= currentMill + 7 * oneDate) {
				sevenDateNum++
			}
		}
	}

	return [threeDateNum, sevenDateNum]
}

/**
 * 获取年度总结路径
 * @returns {string} 年度总结URL
 */
export const getYearReportUrl = () => {
	const iframeUrl =
		env.NODE_ENV === 'development'
			? 'https://middle.m8bj.cn/middle/#/IEBYearEndPoster2024'
			: 'https://safety.meibaokeji.com/middle/#/IEBYearEndPoster2024'
	const {access_token = '', userInfo: {userId = ''} = {}} = store.getters
	return `${iframeUrl}?TENANTID=${
		getDiffEnvConfig().VUE_APP_CHANNELID
	}&Authorization=${access_token}&source=PC&userId=${userId}`
}
